import t from "@/lang/lang";
import { useQpmCatalogItemSetRentingMutation } from "@/services/backend/qpm/catalog/api/catalog-api";
import { useDebouncedDirtyMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  EditableRentingFeeRow,
  EditableRentingFeesTable,
  hasInvalidRentingFeeRows,
  rentingFeeRowsEqual,
  sortedRentingFeeRows,
} from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/editable-renting-fees-table";

export type CatalogItemRentingComponentProps = {
  catalogItemId: string;
  renting?: Record<number, number>;
};

type SetCatalogItemRentingRequest = {
  catalogItemId: string;
  fees: EditableRentingFeeRow[];
};

function toRows(renting?: Record<number, number>): EditableRentingFeeRow[] {
  if (!renting) {
    return [];
  }

  return sortedRentingFeeRows(
    Object.entries(renting)
      .map(([durationMonths, monthly]) => ({
        durationMonths: Number(durationMonths),
        monthly,
      }))
      .filter((fee) => fee.durationMonths > 0),
  );
}

export function CatalogItemRentingComponent({
  catalogItemId,
  renting,
}: CatalogItemRentingComponentProps) {
  const [setRenting, saveState] = useQpmCatalogItemSetRentingMutation();
  const initialRows = useMemo(() => toRows(renting), [renting]);
  const [rows, setRows] = useState<EditableRentingFeeRow[]>(initialRows);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const request = useMemo<SetCatalogItemRentingRequest>(
    () => ({
      catalogItemId,
      fees: sortedRentingFeeRows(rows),
    }),
    [catalogItemId, rows],
  );

  const persistRenting = useCallback(
    (nextRequest: SetCatalogItemRentingRequest) =>
      setRenting({
        catalogItemId: nextRequest.catalogItemId,
        fees: nextRequest.fees,
      }),
    [setRenting],
  );

  const handleRowsChange = useCallback((nextRows: EditableRentingFeeRow[]) => {
    setRows(sortedRentingFeeRows(nextRows));
  }, []);

  useDebouncedDirtyMutationWithPersistenceStateContextUpdate(
    request,
    persistRenting,
    saveState.isLoading,
    saveState.error,
    saveState.isSuccess,
    saveState.reset,
    350,
    {
      toastError: true,
      skip: !catalogItemId || hasInvalidRentingFeeRows(rows),
      isDirty: (current, baseline) =>
        current.catalogItemId !== baseline.catalogItemId ||
        !rentingFeeRowsEqual(current.fees, baseline.fees),
    },
  );

  return (
    <div data-catalog-item-id={catalogItemId} className="flex flex-col gap-3">
      <div>
        <div className="text-base font-semibold">{t("Mietpreise für IoT")}</div>
        <div className="text-muted-foreground text-sm">
          {t("Diese Mietpreise werden als Mietposition berechnet.")}
        </div>
      </div>
      <EditableRentingFeesTable
        rows={rows}
        disabled={!catalogItemId}
        onChange={handleRowsChange}
        emptyText={t("Keine Mietpreise vorhanden")}
        addButtonText={t("Mietpreis hinzufügen")}
      />
    </div>
  );
}
